<template>
    <div class="col-md-12">
        <button class="btn btn-success btn-add" @click="openModal()">
            <i class="fas fa-bell"></i> Nova notificação
        </button>

        <b-row>
            <b-col md="12">
                <b-table
                    striped hover bordered sort-icon-left show-empty
                    :items="tbData"
                    :fields="fields"
                    :filter="filter"
                    :filter-included-fields="filterOn"
                    @filtered="onFiltered"
                    :current-page="currentPage"
                    :per-page="perPage"
                    thead-class="font-weight-bold"
                >
                    <template v-if="loading" #empty>
                        <div class="text-center">
                            <b-spinner variant="dark" small></b-spinner> Processando, por favor, aguarde...
                        </div>
                    </template>

                    <template v-else #empty>
                        <h6 class="empty-message">Nenhum resultado encontrado.</h6>
                    </template>

                    <!-- Evento -->
                    <template #cell(event)="{ item }">
                        <span class="event-label">{{ eventLabel(item) }}</span>
                    </template>

                    <!-- Grupos (badges coloridos por tipo) -->
                    <template #cell(groups)="{ item }">
                        <template v-if="item.groups && item.groups.length > 0">
                            <span
                                v-for="group in item.groups"
                                :key="group.id"
                                class="badge-group"
                                :class="group.type === 'email' ? 'badge-email' : 'badge-whatsapp'"
                            >
                                <i
                                    :class="group.type === 'email' ? 'fas fa-envelope' : 'fas fa-phone'"
                                    class="badge-icon"
                                ></i>
                                {{ group.name }}
                            </span>
                        </template>
                        <span v-else class="text-muted">—</span>
                    </template>

                    <!-- Status com radio ativo/inativo -->
                    <template #cell(active)="{ item }">
                        <div class="status-cell">
                            <span
                                class="status-pill"
                                :class="item.active ? 'pill-active' : 'pill-inactive'"
                            >
                                {{ item.active ? 'Ativo' : 'Inativo' }}
                            </span>
                            <div class="radio-group">
                                <label class="radio-label">
                                    <input
                                        type="radio"
                                        :name="`status-${item.id}`"
                                        :value="true"
                                        :checked="item.active"
                                        @change="toggleStatus(item, true)"
                                    >
                                    <span class="radio-yes">Sim</span>
                                </label>
                                <label class="radio-label">
                                    <input
                                        type="radio"
                                        :name="`status-${item.id}`"
                                        :value="false"
                                        :checked="!item.active"
                                        @change="toggleStatus(item, false)"
                                    >
                                    Não
                                </label>
                            </div>
                        </div>
                    </template>

                    <!-- Data/hora e usuário da última alteração -->
                    <template #cell(updated_at)="{ item }">
                        <div class="updated-date">{{ formatDate(item.updated_at) }}</div>
                        <div class="updated-user" v-if="item.updated_by">
                            <span class="user-avatar">{{ initials(item.updated_by.name) }}</span>
                            {{ item.updated_by.name }}
                        </div>
                        <div class="text-muted small" v-else>—</div>
                    </template>

                    <!-- Botão visualizar -->
                    <template v-slot:cell(view-button)="{ item }">
                        <b-btn class="btn-view" @click.prevent="openModal(item)">
                            <i class="far fa-eye" aria-hidden="true"></i>
                        </b-btn>
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <b-row class="justify-content-between">
            <b-col md="4">
                <b-form-group
                    label="Registros por página"
                    label-for="per-page-select"
                    label-cols-md="6"
                    label-align-sm="right"
                    label-size="sm"
                    class="mb-0 align-self-center"
                >
                    <b-form-select id="per-page-select" v-model="perPage" :options="pageOptions"></b-form-select>
                </b-form-group>
            </b-col>
            <b-col sm="7" md="6" class="my-1">
                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    align="right"
                    size="md"
                    class="my-0"
                ></b-pagination>
            </b-col>
        </b-row>

        <NotificationModal
            ref="notificationModal"
            @updateTable="updateTable"
            @deleteNotification="removeFromTable"
        ></NotificationModal>
    </div>
</template>

<script>
import axios from 'axios';
import NotificationModal from './NotificationModal.vue';

const EVENT_LABELS = {
    request_created: 'Solicitação criada',
    request_in_progress: 'Solicitação em andamento',
    request_reopened: 'Solicitação reaberta',
    request_closed: 'Solicitação fechada',
    status_report: 'Status report',
    monitoring: 'Monitoramento'
};

const SCHEDULED_EVENTS = ['status_report', 'monitoring'];

export default {
    name: "NotificationsTable",
    components: { NotificationModal },
    data() {
        return {
            tbData: [],
            loading: false,
            fields: [
                {
                    key: 'event',
                    label: 'Evento',
                    sortable: true,
                    class: 'text-left align-middle',
                    thStyle: { backgroundColor: '#1c869a', color: '#ffffff' },
                },
                {
                    key: 'groups',
                    label: 'Grupos',
                    sortable: false,
                    class: 'text-left align-middle',
                    thStyle: { backgroundColor: '#1c869a', color: '#ffffff' },
                },
                {
                    key: 'active',
                    label: 'Status',
                    sortable: true,
                    class: 'text-center align-middle',
                    thStyle: { backgroundColor: '#1c869a', color: '#ffffff', width: '160px' },
                },
                {
                    key: 'updated_at',
                    label: 'Última alteração',
                    sortable: true,
                    class: 'text-left align-middle',
                    thStyle: { backgroundColor: '#1c869a', color: '#ffffff', width: '180px' },
                },
                {
                    key: 'view-button',
                    label: 'Visualizar',
                    sortable: false,
                    class: 'text-center align-middle',
                    thStyle: { backgroundColor: '#1c869a', color: '#ffffff', width: '90px' },
                },
            ],
            totalRows: 0,
            currentPage: 1,
            perPage: 10,
            pageOptions: [5, 10, 15, { value: 100, text: 'Ver mais' }],
            filter: null,
            filterOn: [],
        };
    },
    created() {
        this.getDataTable();
    },
    methods: {
        openModal(value = null) {
            this.$refs.notificationModal.openModal(value);
        },
        refresh() {
            this.getDataTable();
        },
        getDataTable() {
            this.loading = true;
            axios.get('/settings/notifications')
                .then(response => {
                    this.tbData = response.data;
                    this.totalRows = response.data.length;
                })
                .catch(error => {
                    console.error(error);
                    this.tbData = [];
                    this.totalRows = 0;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        toggleStatus(item, value) {
            axios.patch(`/settings/notifications/${item.id}/status`, { active: value })
                .then(response => {
                    const found = this.tbData.find(el => el.id === item.id);
                    if (found) Object.assign(found, response.data);
                })
                .catch(error => {
                    console.error(error);
                    Swal.fire({ type: 'error', text: 'Falha ao atualizar status!' });
                });
        },
        updateTable(data) {
            if (!data.id) return;
            const found = this.tbData.find(el => el.id === data.id);
            if (found) {
                Object.assign(found, data);
            } else {
                this.tbData.push(data);
                this.totalRows = this.tbData.length;
            }
            this.$emit('changed');
        },
        removeFromTable(id) {
            this.tbData = this.tbData.filter(el => el.id !== id);
            this.totalRows = this.tbData.length;
            this.$emit('changed');
        },
        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },
        eventLabel(item) {
            const label = EVENT_LABELS[item.event] || item.event;
            if (SCHEDULED_EVENTS.includes(item.event) && item.schedule_time !== null && item.schedule_time !== undefined) {
                const hour = String(item.schedule_time).padStart(2, '0');
                return `${label} - ${hour}h`;
            }
            return label;
        },
        formatDate(value) {
            if (!value) return '—';
            const d = new Date(value);
            if (isNaN(d)) return value;
            return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        },
        initials(name) {
            if (!name) return '?';
            return name.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('');
        },
    },
};
</script>

<style scoped>
.btn-add {
    border-radius: 2px;
}

.empty-message {
    text-align: center;
}

/* Grupos */
.badge-group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 0.78em;
    font-weight: 500;
    margin: 2px 3px;
}

.badge-whatsapp {
    background-color: #d4edda;
    color: #155724;
}

.badge-email {
    background-color: #f8d7da;
    color: #721c24;
}

.badge-icon {
    font-size: 0.9em;
}

/* Status */
.status-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.status-pill {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 20px;
    font-size: 0.78em;
    font-weight: 500;
}

.pill-active {
    background-color: #d4edda;
    color: #155724;
}

.pill-inactive {
    background-color: #e9ecef;
    color: #6c757d;
}

.radio-group {
    display: flex;
    gap: 8px;
}

.radio-label {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.78em;
    cursor: pointer;
    margin-bottom: 0;
    color: #6c757d;
}

.radio-yes {
    color: #155724;
}

/* Última alteração */
.updated-date {
    font-size: 0.82em;
    color: #6c757d;
}

.updated-user {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 3px;
    font-size: 0.82em;
}

.user-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: #cce5ff;
    color: #004085;
    font-size: 0.72em;
    font-weight: 600;
    flex-shrink: 0;
}

/* Botão visualizar */
.btn-view {
    border-radius: 3px;
}
</style>